import { isCollectionBlock } from '@/lib/blocks/collection/blocks';
import { sortBlocksByPosition } from '@/lib/blocks/tree';
import type { PageBlock } from '@/types/block';

function compactSiblingPositions(blocks: PageBlock[], parentBlockId: string | null): PageBlock[] {
  const siblings = sortBlocksByPosition(
    blocks.filter((block) => (block.parentBlockId ?? null) === parentBlockId),
  );

  if (siblings.every((block, index) => block.position === index + 1)) {
    return blocks;
  }

  const updates = new Map<string, PageBlock>();
  siblings.forEach((block, index) => {
    updates.set(block.id, {
      ...block,
      position: index + 1,
    });
  });

  return blocks.map((block) => updates.get(block.id) || block);
}

function collectDescendantBlockIds(blocks: PageBlock[], rootBlockId: string): Set<string> {
  const rootBlock = blocks.find((block) => block.id === rootBlockId);
  if (!rootBlock) {
    return new Set();
  }

  const childIdsByParentId = new Map<string, string[]>();
  blocks.forEach((block) => {
    const parentId = block.parentBlockId;
    if (!parentId) {
      return;
    }

    const childIds = childIdsByParentId.get(parentId) || [];
    childIds.push(block.id);
    childIdsByParentId.set(parentId, childIds);
  });

  const idsToDelete = new Set<string>([rootBlockId]);
  const stack = [rootBlockId];

  while (stack.length > 0) {
    const currentId = stack.pop();
    if (!currentId) {
      continue;
    }

    const childIds = childIdsByParentId.get(currentId) || [];
    childIds.forEach((childId) => {
      if (idsToDelete.has(childId)) {
        return;
      }

      idsToDelete.add(childId);
      stack.push(childId);
    });
  }

  return idsToDelete;
}

export function cleanupParentContainers(
  blocks: PageBlock[],
  startParentBlockId: string | null,
  preserveEmptyParentIds: ReadonlySet<string> = new Set<string>(),
): PageBlock[] {
  let nextBlocks = compactSiblingPositions(blocks, startParentBlockId);
  let currentParentBlockId = startParentBlockId;

  while (currentParentBlockId) {
    const parentBlock = nextBlocks.find((block) => block.id === currentParentBlockId);
    if (!parentBlock) {
      break;
    }

    const children = sortBlocksByPosition(
      nextBlocks.filter((block) => block.parentBlockId === currentParentBlockId),
    );

    if (isCollectionBlock(parentBlock)) {
      // Collections are never auto-removed: card_template child keeps them structurally alive.
      break;
    }

    if (parentBlock.blockType !== 'group') {
      break;
    }

    if (children.length === 0) {
      if (
        preserveEmptyParentIds.has(parentBlock.id) ||
        (parentBlock.uiKey ? preserveEmptyParentIds.has(parentBlock.uiKey) : false)
      ) {
        break;
      }

      const ancestorParentBlockId = parentBlock.parentBlockId ?? null;
      nextBlocks = nextBlocks.filter((block) => block.id !== parentBlock.id);
      nextBlocks = compactSiblingPositions(nextBlocks, ancestorParentBlockId);
      currentParentBlockId = ancestorParentBlockId;
      continue;
    }

    break;
  }

  return nextBlocks;
}

export function removeBlockSubtreeAndCleanup(
  blocks: PageBlock[],
  blockId: string,
  preserveEmptyParentIds: ReadonlySet<string> = new Set<string>(),
): PageBlock[] {
  const blockToDelete = blocks.find((block) => block.id === blockId);
  if (!blockToDelete) {
    return blocks;
  }

  const blockIdsToDelete = collectDescendantBlockIds(blocks, blockId);
  const nextBlocks = blocks.filter((block) => !blockIdsToDelete.has(block.id));

  return cleanupParentContainers(
    nextBlocks,
    blockToDelete.parentBlockId ?? null,
    preserveEmptyParentIds,
  );
}
